import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Facebook, Loader2, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Australia/Melbourne",
      hour12: true,
    }).format(new Date(iso)).toLowerCase().replace(" ", "");
  } catch {
    return "";
  }
};

const fmtTimeRange = (startIso, endIso) => {
  const start = fmtTime(startIso);
  const end = fmtTime(endIso);
  if (start && end) return `${start} – ${end}`;
  return start || "";
};

export const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/events`)
      .then((r) => {
        if (alive) setEvents(r.data || []);
      })
      .catch((e) => console.error("Failed to load events", e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="events"
      data-testid="upcoming-events-section"
      className="relative py-24 sm:py-32 px-6 sm:px-10 bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
              / Upcoming Events
            </span>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FDFBF7] uppercase mt-4 leading-[0.9]">
              What's next
              <br />
              <span className="text-[#F5A623]">on the bill.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#FDFBF7]/65 text-base leading-relaxed">
            Tap any event to RSVP on our Facebook page. Save the date, bring
            your crew.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#FDFBF7]/50">
            <Loader2 className="animate-spin mr-3" /> Loading events…
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 text-[#FDFBF7]/60" data-testid="events-empty">
            No events scheduled right now — check back soon, or follow us on Facebook.
          </div>
        ) : (
          <div className="border-t border-white/10">
            {events.map((event, i) => {
              const timeRange = fmtTimeRange(event.start_date_time, event.end_date_time);
              return (
                <motion.a
                  key={event.id}
                  href={event.fbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`event-link-${event.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="group block border-b border-white/10 py-7 sm:py-8 px-2 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center gap-5 sm:gap-8">
                    <div className="text-center border-r border-white/15 pr-5 sm:pr-8 min-w-[70px]">
                      <div className="font-display text-4xl sm:text-5xl text-[#F5A623] leading-none">
                        {event.date?.day}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/60 mt-1 font-semibold">
                        {event.date?.month}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#F5A623] border border-[#F5A623]/60 px-2 py-0.5">
                          {event.type}
                        </span>
                        {timeRange && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/70 font-semibold">
                            <Clock size={11} /> {timeRange}
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-2xl sm:text-3xl uppercase text-[#FDFBF7] group-hover:text-[#F5A623] transition-colors leading-tight">
                        {event.title}
                      </h3>
                      {event.performer && event.performer !== event.title && (
                        <p className="mt-1 text-sm text-[#F5A623]/85 uppercase tracking-wider font-semibold">
                          With {event.performer}
                        </p>
                      )}
                      {event.description && (
                        <p className="hidden sm:block mt-2 text-[#FDFBF7]/60 text-base">
                          {event.description}
                        </p>
                      )}
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/50 font-semibold">
                      <Facebook size={16} />
                      <span>Event</span>
                    </div>
                    <div className="text-[#FDFBF7]/40 group-hover:text-[#F5A623] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all">
                      <ArrowUpRight size={32} strokeWidth={1.5} />
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </div>
        )}

        <p className="text-center text-sm text-[#FDFBF7]/50 mt-10" data-testid="events-footer-note">
          More dates dropping every fortnight — follow us on Facebook so you
          don't miss out.
        </p>
      </div>
    </section>
  );
};
